import { useState, useCallback, useContext, useLayoutEffect } from 'react';
import { GlobalContext } from '../../App';

export const useIndexedDB = (dbName, dbVersion, storeName) => {
  const [db, setDb] = useState(null);

  useLayoutEffect(() => {
    const request = indexedDB.open(dbName, dbVersion);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: 'id' });
      }
    };

    request.onsuccess = (event) => {
      setDb(event.target.result);
    };

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.errorCode);
    };
  }, [dbName, dbVersion, storeName]);

  return db;
};

export const useAddData = (db, storeName) => {
  return useCallback(
    (data) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const getRequest = store.get(data?.id);

        getRequest.onsuccess = (event) => {
          if (event.target.result) {
            // Key exists, update the data
            const updateRequest = store.put(data);
            updateRequest.onsuccess = () => {
              db.dispatchEvent(new Event('datachange'));
              resolve();
            };
            updateRequest.onerror = (event) => reject(event.target.error);
          } else {
            // Key does not exist, add the data
            const addRequest = store.add(data);
            addRequest.onsuccess = () => {
              db.dispatchEvent(new Event('datachange'));
              resolve();
            };
            addRequest.onerror = (event) => reject(event.target.error);
          }
        };

        getRequest.onerror = (event) => reject(event.target.error);
      }),
    [db, storeName]
  );
};

export const useUpdateData = (db, storeName) => {
  return useCallback(
    (data) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put(data);

        request.onsuccess = () => {
          db.dispatchEvent(new Event('datachange'));
          resolve();
        };
        request.onerror = (event) => reject(event.target.error);
      }),
    [db, storeName]
  );
};

export const useGetData = (db, storeName) => {
  return useCallback(
    (id) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(id);

        request.onsuccess = (event) => resolve(event.target.result);
        request.onerror = (event) => reject(event.target.error);
      }),
    [db, storeName]
  );
};

export const useGetDataByName = (db, storeName) => {
  return useCallback(
    (name) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.openCursor();
        const results = [];

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            if (cursor.value.name === name) {
              results.push(cursor.value);
            }
            cursor.continue();
          } else {
            resolve(results);
          }
        };

        request.onerror = (event) => reject(event.target.error);
      }),
    [db, storeName]
  );
};

export const useIndexedDBSync = (storeName, keyName) => {
  const { db } = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const getDataByName = useGetDataByName(db, storeName);

  useLayoutEffect(() => {
    const loadData = async () => {
      if (!db) {
        console.log('step 2 db: ', db);
        return;
      }
      
      try {
        const result = await getDataByName(keyName);
        setData(result[0]?.value || []);
      } catch (error) {
        console.error('Error loading data from IndexedDB:', error);
      }
    };
    
    loadData();
    
    const handleDataChange = () => {
      loadData();
    };
    
    if (db) {
      db.addEventListener('datachange', handleDataChange);
    }
    
    return () => {
      if (db) {
        db.removeEventListener('datachange', handleDataChange);
      }
    };
  }, [db, getDataByName, storeName, keyName]);

  return data;
};

export const useIndexedDBSyncV2 = (storeName, keyName) => {
  const { db } = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const getDataByName = useGetDataByName(db, storeName);

  useLayoutEffect(() => {
    const loadData = async () => {
      if (!db) {
        console.log('step 2 db: ', db);
        return;
      }
      
      try {
        const result = await getDataByName(keyName);
        setData(result[0]?.value || []);
      } catch (error) {
        console.error('Error loading data from IndexedDB:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    
    const handleDataChange = () => {
      loadData();
    };
    
    if (db) {
      db.addEventListener('datachange', handleDataChange);
    }
    
    return () => {
      if (db) {
        db.removeEventListener('datachange', handleDataChange);
      }
    };
  }, [db, getDataByName, storeName, keyName]);

  return { data, loading };
};

export const updateOrAddItem = async (db, storeName, newItem, dataField, primaryId) => {
  // let primaryId = id

  // dataField === 'dataGraves' ? primaryId = 7 : null
  
  return new Promise(async (resolve, reject) => {
    try {
      // Step 1: קבלת הרשומות הקיימות
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const getRequest = store.get(primaryId); // נניח שה-id של הרשומה הוא 1

      getRequest.onsuccess = (event) => {
        const existingData = event?.target?.result;

        if (existingData) {
          // Step 2: אם יש נתונים, עדכן או הוסף את האיבר
          let updatedCemeteries = existingData.value || [];

          // בדיקה אם האיבר כבר קיים לפי ID (נניח שכל איבר ברשימה מכיל שדה 'id')
          const index = updatedCemeteries?.findIndex((item) => item?.unicId === newItem?.unicId);
          // const index = updatedCemeteries?.findIndex((item) => item?.id === newItem?.id);

          if (index !== -1) {
            // אם האיבר קיים, נעדכן אותו
            updatedCemeteries[index] = newItem;
          } else {
            // אם האיבר לא קיים, נוסיף אותו לרשימה
            updatedCemeteries.push(newItem);
          }

          // Step 3: עדכון הרשומה ב-IndexedDB
          const updateRequest = store.put({ id: primaryId, name: dataField, value: updatedCemeteries });
          updateRequest.onsuccess = () => {
            db.dispatchEvent(new Event('datachange'));
            resolve(updatedCemeteries);
          };
          updateRequest.onerror = (event) => reject(event.target.error);
        } else {
          // אם הרשומה לא קיימת, ניצור רשומה חדשה עם האיבר החדש
          const newData = { id: primaryId, name: dataField, value: [newItem] };
          const addRequest = store.add(newData);
          addRequest.onsuccess = () => {
            db.dispatchEvent(new Event('datachange'));
            resolve([newItem]);
          };
          addRequest.onerror = (event) => reject(event.target.error);
        }
      };

      getRequest.onerror = (event) => reject(event.target.error);
    } 
    catch (error) {
      console.error('Error updating or adding item:', error);
      reject(error);
    }
  });
};

export const removeItem = async (db, storeName, itemId, primaryId) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Step 1: קבלת הרשומות הקיימות
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const getRequest = store.get(primaryId);

      getRequest.onsuccess = (event) => {
        const existingData = event?.target?.result;

        if (existingData) {
          // Step 2: הסרת האייטם מהרשימה
          let updatedCemeteries = existingData.value || [];
          updatedCemeteries = updatedCemeteries.filter((item) => item?.id !== itemId);

          // Step 3: עדכון הרשומה עם הרשימה המעודכנת
          const updateRequest = store.put({ id: primaryId, name: existingData.name, value: updatedCemeteries });

          updateRequest.onsuccess = () => {
            db.dispatchEvent(new Event('datachange'));
            resolve(updatedCemeteries);
          };
          updateRequest.onerror = (event) => reject(event.target.error);
        } else {
          // Step 4: אם הרשומה לא קיימת
          reject(new Error('Record not found'));
        }
      };

      getRequest.onerror = (event) => reject(event.target.error);
    } 
    catch (error) {
      console.error('Error removing item:', error);
      reject(error);
    }
  });
};







